import React, { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { usePlatformSettings } from "@/hooks/usePlatformSettings";
import { useCryptoNetworks } from "@/hooks/useCryptoNetworks";
import { useFiatConfig } from "@/hooks/useFiatConfig";
import { useFiatRates } from "@/hooks/useFiatRates";
import { useRealtimeBalances } from "@/hooks/useRealtimeBalances";
import { useInvalidateWalletBalances } from "@/hooks/useEnvironmentWallets";
import { useCurrencyPrecision } from "@/hooks/useCurrencyPrecision";

const noop = () => {};

/**
 * Mounted once inside the authenticated app shell.
 *
 * - Warms the shared valuation inputs (networks, fiat config, FX rates,
 *   platform settings) so no individual page has to wait for them.
 * - Owns a single wallet realtime subscription for the whole app; every
 *   balance-backed query is invalidated from here, so all modules stay in sync
 *   without each page opening its own channel.
 */
export default function BalanceSyncProvider({ children }: { children: React.ReactNode }) {
  const { user, session, loading: authLoading } = useAuth();
  const { isSandbox } = usePlatformSettings();
  const qc = useQueryClient();
  const invalidateBalances = useInvalidateWalletBalances();

  // Warm shared configuration/valuation inputs app-wide.
  useCryptoNetworks();
  useCurrencyPrecision();
  useFiatConfig();
  useFiatRates();

  const walletTable = isSandbox ? "sandbox_wallets" : "wallets";

  // Single global wallet subscription (keeps the react-query cache authoritative).
  useRealtimeBalances(user?.id, noop, walletTable as "wallets" | "sandbox_wallets");

  // Money-movement tables that change a balance without touching the wallet row
  // first (deposits crediting, withdrawals debiting/refunding).
  useEffect(() => {
    if (!user?.id || authLoading || !session) return;
    const uid = user.id;

    const refresh = () => {
      invalidateBalances();
      qc.invalidateQueries({ queryKey: ["crypto-transactions"] });
      qc.invalidateQueries({ queryKey: ["withdrawal-requests"] });
      qc.invalidateQueries({ queryKey: ["transactions"] });
    };

    const channel = supabase
      .channel(`global-balance-sync-${uid}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "crypto_transactions", filter: `user_id=eq.${uid}` },
        refresh
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "withdrawal_requests", filter: `user_id=eq.${uid}` },
        refresh
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "transactions", filter: `user_id=eq.${uid}` },
        refresh
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, authLoading, session, qc]);

  return <>{children}</>;
}
